import Link from 'next/link';
import { Fragment, useState } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { ChaptersDropdownFilter } from 'components/common/tasks';
import { DiscussionSummary } from '.';

import type { Chapter } from 'shared-types';

export type Discussion = {
  chapter: Pick<Chapter, 'id' | 'publishDate' | 'slug' | 'title'> & {
    number: number;
  };
  data: { format: string; text: string; title?: string };
  id: number;
  lastReply: string;
  numberOfComments: number;
  numberOfOwnComments: number;
};

interface Props {
  chapters: (Chapter & { number: number })[];
  discussions: Discussion[];
  loading?: boolean;
}

const DiscussionContainer = ({
  chapters,
  discussions,
  loading = false
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { courseSlug },
    theme
  } = useReduxState();

  const [selectedChapterId, setSelectedChapterId] = useState<number>(null);

  const handleChapterChange = (selectedChapterId: number) =>
    setSelectedChapterId(selectedChapterId);

  let filteredDiscussions = discussions || [];
  if (selectedChapterId && discussions?.length)
    filteredDiscussions = discussions.filter(
      (discussion) => discussion.chapter.id === selectedChapterId
    );

  return (
    <Box
      headerContent={
        <ChaptersDropdownFilter
          chapters={chapters}
          onChange={handleChapterChange}
        />
      }
      headerContentWrap={true}
      loading={loading}
      padding={15}
      title={messages.tasksDiscussions}
    >
      {filteredDiscussions?.length ? (
        <div className="discussions-container">
          {filteredDiscussions.map((discussion, i) => (
            <Fragment key={discussion.id}>
              {i === 0 ||
              discussion.chapter.number !==
                filteredDiscussions[i - 1].chapter.number ? (
                <div className="chapter-title">
                  {`${discussion.chapter.number}. ${discussion.chapter.title}`}
                  {!discussion.chapter.publishDate ? (
                    <div className="chapter-title-status">
                      {`(${messages.commonNotPublished})`}
                    </div>
                  ) : Date.parse(discussion.chapter.publishDate as string) >
                    dateNow ? (
                    <div className="chapter-title-status">
                      {`(${messages.commonWillPublish} ${getFormattedDate({
                        date: discussion.chapter.publishDate
                      })})`}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="discussion-link">
                <Link
                  href={`/admin/courses/${courseSlug}/follow/discussions/${discussion.id}`}
                >
                  <DiscussionSummary discussion={discussion} />
                </Link>
              </div>
            </Fragment>
          ))}
        </div>
      ) : (
        <EmptyIndicator
          description={messages.tasksNoDiscussionsDescription}
          icon="comment"
          title={messages.tasksNoDiscussions}
        />
      )}

      <style jsx>{`
        .chapter-title {
          color: ${theme.textColorLight};
          font-size: 1.8rem;
          font-weight: 700;
          margin: 60px 0 20px;
          padding: 0 5px;
        }
        .chapter-title:first-child {
          margin-top: 10px;
        }
        .chapter-title-status {
          color: ${theme.textColorLighter};
          display: inline;
          font-size: 1.4rem;
          font-weight: 400;
          margin-left: 7px;
          position: relative;
          top: -1px;
        }
        .discussion-link + .discussion-link {
          margin-top: 40px;
        }
        .discussion-link :global(a) {
          border-radius: ${theme.cardBorderRadius};
          color: inherit;
          display: block;
          text-decoration: none;
          transition: box-shadow 0.3s;
        }
        .discussion-link :global(a:focus),
        .discussion-link :global(a:hover) {
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
        }
        @media (min-width: ${breakpoints.small}px) {
          .discussions-container {
            margin: 10px;
          }
        }
      `}</style>
    </Box>
  );
};

export default DiscussionContainer;
